import { isPortalFamilyHostLabel, PORTAL_TENANT_HEADER } from "@l30/shared";

import { portalHostLabel, resolvePortalTenantSlug } from "../lib/portal-tenant-path.js";

import type { ApiErrorResponse } from "@l30/shared";

/**
 * Portal fetch wrapper + error classes.
 *
 * Self-contained — no dependency on the staff `apiFetch` (which has
 * Clerk-token retry semantics that don't apply here). The portal
 * auth model is a cookie set by the magic-link verify endpoint;
 * `credentials: "include"` makes the browser send it.
 *
 * Path is relative to `/api`. 4xx/5xx parses ApiErrorResponse and
 * throws `ApiError`; if `responseSchema` is provided it validates
 * the body (canon 04-shared/ssot.md R6) and throws
 * `ApiContractError` on mismatch.
 */
interface ResponseSchema<T> {
  safeParse(data: unknown):
    | { success: true; data: T }
    | {
        success: false;
        error: { issues: ReadonlyArray<{ path: PropertyKey[]; message: string }> };
      };
}

/**
 * Base URL for API calls. The portal SPA lives on
 * `<slug>.portal.latitude30.co` but the API is hosted via the staff
 * SWA's linked-backend at `insights-dev.latitude30.co` (dev) /
 * `app.latitude30.co` (prod) — see
 * docs/enhancements/portal-swa-split-2026-05.md for why.
 *
 * `VITE_PORTAL_API_BASE_URL` is set per-env at build time by the
 * portal-deploy CI workflow. When empty (local dev with `pnpm
 * dev:portal`), we fall back to relative `/api/...` paths and
 * Vite's proxy hands them off to the local API on `:3001`.
 *
 * Portal-session cookies are scoped to the apex `.latitude30.co` by
 * the verify endpoint so they accompany cross-origin requests from
 * portal hosts → API host.
 */
const API_BASE_URL = (import.meta.env.VITE_PORTAL_API_BASE_URL ?? "").replace(/\/$/, "");

export async function portalFetch<T>(
  path: string,
  options: RequestInit = {},
  responseSchema?: ResponseSchema<T>,
): Promise<T> {
  const headers = new Headers(options.headers);

  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  // Family hosts (`devices.portal…` / `gop.portal…`) cannot resolve a
  // leaf tenant from Origin alone — send the path slug. Leaf hosts
  // keep Origin-only resolution; never override them with a header.
  const hostLabel = typeof window !== "undefined" ? portalHostLabel(window.location.host) : null;
  const tenantSlug = resolvePortalTenantSlug();
  if (
    tenantSlug &&
    hostLabel &&
    isPortalFamilyHostLabel(hostLabel) &&
    !headers.has(PORTAL_TENANT_HEADER)
  ) {
    headers.set(PORTAL_TENANT_HEADER, tenantSlug);
  }

  // Localhost / preview: always send path tenant when present so the
  // API can scope without a portal-shaped Origin.
  if (
    tenantSlug &&
    typeof window !== "undefined" &&
    !hostLabel &&
    !headers.has(PORTAL_TENANT_HEADER)
  ) {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
      headers.set(PORTAL_TENANT_HEADER, tenantSlug);
    }
  }

  const response = await fetch(`${API_BASE_URL}/api${path}`, {
    ...options,
    headers,
    // Cookie carries the portal session; without this the browser
    // strips it on cross-origin requests. The verify endpoint sets
    // the cookie with `domain=.latitude30.co` so it's sent here
    // even when the API host is a different subdomain.
    credentials: "include",
  });

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => null)) as ApiErrorResponse | null;
    const code = errorBody?.error?.code ?? "UNKNOWN";
    const message = errorBody?.error?.message ?? `API error: ${response.status}`;
    const requestId = errorBody?.error?.requestId ?? null;
    // 401/403 mean the portal-session cookie is missing, expired, or no
    // longer authorises this request. Surface these as a distinct type
    // so the global QueryCache handler can route to sign-in instead of
    // each page offering a Retry that can never succeed (assessment U1).
    // Exception: public anti-abuse 403s (Turnstile on magic-link
    // request) are not session-expiry — fall through to plain ApiError
    // so the sign-in form can show a retry message without bouncing.
    if (
      (response.status === 401 || response.status === 403) &&
      code !== "MAGIC_LINK_TURNSTILE_FAILED"
    ) {
      throw new PortalAuthError(code, message, response.status, requestId);
    }
    throw new ApiError(code, message, response.status, requestId);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const body = (await response.json()) as unknown;

  if (responseSchema) {
    const parsed = responseSchema.safeParse(body);
    if (!parsed.success) {
      throw new ApiContractError(path, parsed.error.issues);
    }
    return parsed.data;
  }

  return body as T;
}

/**
 * Structured API error. Carries the envelope `code` + `requestId` so
 * the `PortalQueryError` primitive can surface a support-greppable
 * failure (mirrors the staff app's `ApiError`).
 */
export class ApiError extends Error {
  public readonly requestId: string | null;

  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    requestId: string | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.requestId = requestId;
  }
}

/**
 * Auth-expiry error — thrown for 401/403. Distinct subclass so the
 * global QueryCache handler (and the `/me` gate) can tell "your
 * session is gone, go to sign-in" apart from a transient 5xx that
 * should offer a Retry rather than a bounce (assessment U1).
 */
class PortalAuthError extends ApiError {
  constructor(code: string, message: string, status: number, requestId: string | null = null) {
    super(code, message, status, requestId);
    this.name = "PortalAuthError";
  }
}

/**
 * Type guard — is this failure an expired/invalid portal session?
 * Used by the QueryCache onError and the portal shell to route to
 * sign-in instead of rendering a dead Retry.
 */
export function isPortalAuthError(error: unknown): error is PortalAuthError {
  return error instanceof PortalAuthError;
}

/**
 * Thrown when an API response doesn't match the expected Zod schema.
 * Canon: 04-shared/ssot.md R6 — drift surfaces here rather than
 * silently propagating through the UI.
 */
class ApiContractError extends Error {
  constructor(
    public readonly path: string,
    public readonly issues: ReadonlyArray<{ path: PropertyKey[]; message: string }>,
  ) {
    const summary = issues
      .slice(0, 3)
      .map((i) => `${i.path.map(String).join(".") || "(root)"}: ${i.message}`)
      .join("; ");
    super(`Response from ${path} doesn't match expected shape: ${summary}`);
    this.name = "ApiContractError";
  }
}
